<script setup>
import { defineProps, defineEmits, useSlots, computed } from 'vue';

const emit = defineEmits(['onSelect', 'onHover']);

const props = defineProps({
  /**
   * Data của item đc truyền
   */
  item: {
    type: Object,
    required: true,
  },
  /**
   * Idx của item
   */
  itemIdx: {
    type: Number,
    default: 0,
  },
  /**
   * Idx của item đc hover
   */
  hoverIdx: {
    type: Number,
    default: -1,
  },
  /**
   * Trường chứa ID
   */
  valueField: {
    type: String,
    required: true,
  },
  /**
   * Trường để hiển thị
   */
  displayField: {
    type: String,
    required: true,
  },
  /**
   * Có đc chọn ko
   */
  isSelected: {
    type: Boolean,
    default: false,
  },
  /**
   * Có hiển thị icon check ko
   */
  isShowIconCheck: {
    type: Boolean,
    default: false,
  },
  /**
   * Có hightlight các item đc select ko
   */
  isHightlightSelectedItem: {
    type: Boolean,
    default: true,
  },
});

const slots = useSlots();

/**
 * Có truyền slot item hay ko
 * @author: BMThang(06/11/2022)
 */
const hasSlotItem = computed(() => {
  return slots['item'] ? true : false;
});

/**
 * Emit event onSelect
 * @param {object} itemData Dữ liệu của item cần chọn
 * @author: BMThang(18/11/2022)
 */
const handleSelectItem = (itemData) => {
  emit('onSelect', itemData, !props.isSelected);
};

/**
 * Emit event onHover
 * @param {object} itemData Dữ liệu của item đc hover
 * @author: BMThang(18/11/2022)
 */
const handleHover = (itemData) => {
  emit('onHover', itemData);
};
</script>

<template>
  <div 
    class="ms-combobox-tree-multiple-item"
  >
    <!-- Main -->
    <div 
      :id="`ms-combobox-tree-multiple-item-${itemIdx}`"
      :class="[
        'ms-combobox-tree-multiple-item-main',
        {'ms-combobox-tree-multiple-item-main-hover': hoverIdx === itemIdx},
        {'checked': isSelected && isHightlightSelectedItem },
      ]"
      @mousemove="handleHover(item)"
      @click="handleSelectItem(item)"
    >
      <!-- Custom item -->
      <slot
        v-if="hasSlotItem"
        :item-data="item"
        :is-selected="isSelected"
        name="item" 
      />
      <!-- Normal item -->
      <template v-else>
        <!-- Text -->
        <div 
          
          :class="[
            'ms-combobox-tree-multiple-item-text', 
          ]"
          :title="item[displayField]"
        >
          {{ item[displayField] }}
        </div>

        <!-- Icon check -->
        <div 
          v-if="isShowIconCheck"
          class="cell-checkbox" 
        >
          <div 
            class="icon" 
            :class="[
              'ms-combobox-tree-multiple-item-check-icon',
              {'checked': isSelected },
            ]"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/scss/_variables.scss';
@import '@/assets/scss/transitions.scss';

.ms-combobox-tree-multiple-item {
  cursor: pointer;
  .ms-combobox-tree-multiple-item-main {
    display: flex;
    align-items: center;
    height: $input-height;
    width: 100%;
    border-radius: 4px;
    .ms-combobox-tree-multiple-item-text {
      flex-grow: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;
      height: 100%;
      display: block;
      padding: 0 6px;
      margin-left: 4px;
      font-size: $font-size-body;
      font-weight: $font-weight-normal;
      line-height: $input-height;
      color: $combobox-hover-color;
      cursor: pointer;
    }
    .ms-combobox-tree-multiple-item-check-icon {
      margin-right: 4px;
    }
    &.checked {
      background-color: var(--combobox-selected-bg-light);
    }
    &.ms-combobox-tree-multiple-item-main-hover {
      background-color: var(--combobox-hover-bg);
    }
  }
}
</style>